import {FC, useRef, useState} from 'react'
import {KTSVG} from '../../../_metronic/helpers'
import Swal from 'sweetalert2'
import useAccessToken from '../../../hooks/AccessToken'
import {useIntl} from 'react-intl'
import {ITemplateAnalytic} from '../../../interfaces/Analytics/Analytics'
import {useAnalyticsStore} from '../../../stores/Analytics/AnalyticStore'
import {deleteTemplate} from '../../../api/Analitik/TemplateCRUD'
import {useFormik} from 'formik'
import useHandleCloseModal from '../../../hooks/HandleCloseModal'
import {handleCaps, truncateText} from '../../../functions/general'
import * as Yup from 'yup'
import clsx from 'clsx'
import { mutate } from 'swr'

interface IProps {
  template: ITemplateAnalytic
}

const DeleteTemplate: FC<IProps> = ({template}) => {
  const API_URL = process.env.REACT_APP_API_URL

  const closeModalRef = useRef<HTMLButtonElement>(null)
  const modalRef = useRef<HTMLButtonElement>(null)
  const [modalBtnLoading, setModalBtnLoading] = useState(false)
  const token = useAccessToken()
  const {setTemplatesAnalytic} = useAnalyticsStore()
  const intl = useIntl()
  const formik = useFormik({
    initialValues: {
      name: '',
    },
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .required(
          intl.formatMessage(
            {id: 'FORM.VALIDATION.FIELD_REQUIRED'},
            {title: intl.formatMessage({id: 'FORM.LABEL.NAME'})}
          )
        )
        .test(
          'match-template',
          intl.formatMessage({id: 'FORM.VALIDATION.NAME_MUST_MATCH_TEMPLATE'}),
          function (value) {
            return value === template.name
          }
        ),
    }),
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setSubmitting(true)
      setModalBtnLoading(true)
      try {
        const response = await deleteTemplate(template.id, token)
        if (response.data.success) {
          Swal.fire({
            icon: 'success',
            title: intl.formatMessage({id: 'ALERT.TITLE.SUCCESS'}),
            text: intl.formatMessage({id: 'FORM.VALIDATION.DELETE_SUCCESS'}),
            heightAuto: false,
          })
          mutate(`${API_URL}/menu-analytics/limit`)

          closeModalRef.current?.click()
          setTemplatesAnalytic(response.data.data)
        }
      } catch (error: any) {
        Swal.fire({
          icon: 'error',
          title: error.response.data.message,
          confirmButtonText: 'Ok',
        })
      } finally {
        mutate(`${API_URL}/menu-analytics/limit`)

        setModalBtnLoading(false)
      }
    },
  })

  const {handleCloseModal} = useHandleCloseModal(modalRef, closeModalRef, formik)

  const handleDeleteSection = async () => {
    setModalBtnLoading(true)
    try {
      const response = await deleteTemplate(template.id, token)
      if (response.data.success) {
        Swal.fire({
          icon: 'success',
          title: intl.formatMessage({id: 'ALERT.TITLE.SUCCESS'}),
          text: intl.formatMessage({id: 'FORM.VALIDATION.DELETE_SUCCESS'}),
          heightAuto: false,
        })
        closeModalRef.current?.click()
        setTemplatesAnalytic(response.data.data)
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: error.response.data.message,
        confirmButtonText: 'Ok',
      })
    } finally {
      setModalBtnLoading(false)
    }
  }

  return (
    <>
      <div className='modal fade' tabIndex={-1} id={`delete-template-modal-${template.id}`}>
        <div className='modal-dialog modal-dialog-centered'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title'>
                {intl.formatMessage({id: 'FORM.ACTION.DELETE'})} {truncateText( template.name,25)}
              </h5>
              <div
                className='btn btn-icon btn-sm btn-active-light-primary ms-2'
                data-bs-dismiss='modal'
                aria-label='Close'
              >
                <KTSVG
                  path='/media/icons/duotune/arrows/arr061.svg'
                  className='svg-icon svg-icon-2x'
                />
              </div>
            </div>
            <div className='modal-body'>
              <p className=' mb-0'>
                {intl.formatMessage({id:"FORM.CONFIRMATION.DELETE_TEMPLATE_ANALITIK"})}
              </p>
            </div>
            <form onSubmit={formik.handleSubmit} noValidate>
              <div className='px-4'>
                <div className='input-group '>
                  <input
                    placeholder={template.name}
                    {...formik.getFieldProps('name')}
                    className={clsx('form-control form-control-lg', {
                      'is-invalid': formik.touched.name && formik.errors.name,
                    })}
                    type='text'
                    name='name'
                    autoComplete='off'
                    onInput={(e) => handleCaps(e)}
                  />
                </div>
                {formik.touched.name && formik.errors.name && (
                  <div className='fv-plugins-message-container text-danger'>
                    <span role='alert' className='text-danger'>
                      {formik.errors.name}
                    </span>
                  </div>
                )}
              </div>
              <div className='modal-footer'>
                <button
                  type='button'
                  className='btn btn-light'
                  data-bs-dismiss='modal'
                  ref={closeModalRef}
                >
                  {intl.formatMessage({id: 'FORM.ACTION.CANCEL'})}
                </button>
                <button
                  type='button'
                  className={`btn btn-danger me-10 ${modalBtnLoading ? 'disabled' : ''}`}
                  data-kt-indicator={modalBtnLoading ? 'on' : 'off'}
                  onClick={handleDeleteSection}
                  disabled={!!(formik.errors.name || !formik.touched.name)}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'FORM.ACTION.DELETE'})}
                  </span>
                  <span className='indicator-progress'>
                    Loading...{' '}
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
}

export {DeleteTemplate}
